// frontend/src/pages/ContasAReceberPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useApi from '../hooks/useApi';
import { 
    PlusCircle, X, Loader2, DollarSign, Edit, Trash2, Info, 
    AlertTriangle, CheckCircle, Receipt, Banknote, KeySquare,
    ChevronDown, ChevronUp, Search, ArrowLeft, ArrowRight, Send,
    Calendar, Filter, RefreshCw, ChevronLeft, ChevronRight 
} from 'lucide-react';

// IMPORTA O NOVO COMPONENTE REUTILIZÁVEL (Para Criar e Editar)
import NovoRecebimentoModal from '../components/modals/NovoRecebimentoModal';

// --- Helper para formatar data em UTC (Evita o problema de dia anterior) ---
const formatDateUTC = (dateString) => {
    if (!dateString) return '-';
    // Garante que a data seja interpretada como UTC para não subtrair o fuso horário
    // Se vier com hora (ex: 2025-12-13T00:00:00.000Z), cortamos para pegar só a data YYYY-MM-DD
    const dateOnly = dateString.split('T')[0]; 
    return new Date(dateOnly).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// --- HOOK: useDebounce ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
};

// --- COMPONENTE: MODAL DE CONFIRMAÇÃO DE EXCLUSÃO ---
const ConfirmDeleteModal = ({ onClose, onConfirm, loading, error }) => {
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="text-red-500" size={24} />
                        <h2 className="text-xl font-bold text-gray-800">Excluir Recebimento</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <div className="p-6">
                    {error ? (
                        <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>
                    ) : (
                        <>
                            <p className="text-gray-600">Tem certeza de que deseja excluir esta fatura e todas as suas parcelas? Esta ação não pode ser desfeita.</p>
                            <p className="mt-2 p-3 text-sm text-yellow-800 bg-yellow-100 rounded-md">
                                <span className="font-bold">Atenção:</span> Só é possível excluir faturas que não tenham nenhuma parcela recebida.
                            </p>
                        </>
                    )}
                </div>
                <div className="flex justify-end gap-2 p-4 bg-gray-50 border-t">
                    <button onClick={onClose} disabled={loading} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                    {!error && (
                        <button onClick={onConfirm} disabled={loading} className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-red-600 rounded-md disabled:bg-red-300">
                            {loading ? <Loader2 className="animate-spin" size={18} /> : 'Sim, Excluir'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE: MODAL DE RECEBIMENTO (BAIXA DE PAGAMENTO) ---
const RecebimentoModal = ({ onClose, onSave, fatura, accounts, formatCurrency, selectedCurrency, accountTypes }) => {
    const { apiFetch } = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [selectedParcelaIds, setSelectedParcelaIds] = useState([]);
    const [valorRecebidoTotal, setValorRecebidoTotal] = useState(0);
    const [descontoTotal, setDescontoTotal] = useState(0);
    const [jurosTotal, setJurosTotal] = useState(0);
    const [dataRecebimento, setDataRecebimento] = useState(new Date().toISOString().split('T')[0]);
    const [accountId, setAccountId] = useState('');

    const contasRecebimento = useMemo(() => {
        const tiposCartao = (accountTypes || [])
            .filter(at => at.name.toLowerCase().includes('cartão'))
            .map(at => at.id);
        return accounts.filter(a => a.status === 'ativo' && a.currency === selectedCurrency && !tiposCartao.includes(a.account_type_id));
    }, [accounts, selectedCurrency, accountTypes]);

    const parcelasPendentes = useMemo(() => {
        return fatura.parcelas.filter(p => p.status === 'a vencer' || p.status === 'vencida');
    }, [fatura.parcelas]);

    useEffect(() => {
        const total = parcelasPendentes
            .filter(p => selectedParcelaIds.includes(p.id))
            .reduce((acc, p) => acc + p.valor_parcela, 0);
        setValorRecebidoTotal(total - descontoTotal + jurosTotal);
    }, [selectedParcelaIds, parcelasPendentes, descontoTotal, jurosTotal]);

    const handleSelectParcela = (parcelaId) => {
        setSelectedParcelaIds(prev => prev.includes(parcelaId) ? prev.filter(id => id !== parcelaId) : [...prev, parcelaId]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (selectedParcelaIds.length === 0) { setError('Selecione pelo menos uma parcela.'); setLoading(false); return; }
        if (!accountId) { setError('Selecione a conta.'); setLoading(false); return; }
        if (!dataRecebimento) { setError('Informe a data do recebimento.'); setLoading(false); return; }

        const recebimentoData = {
            parcela_ids: selectedParcelaIds,
            account_id: parseInt(accountId, 10),
            data_recebimento: dataRecebimento,
            valor_recebido_total: parseFloat(valorRecebidoTotal) || 0,
            desconto_total: parseFloat(descontoTotal) || 0,
            juros_total: parseFloat(jurosTotal) || 0,
        };

        try {
            await apiFetch('/contas-a-receber/baixar', { method: 'POST', body: recebimentoData });
            onSave();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6">
                <h2 className="text-xl font-bold mb-4">Baixar Pagamento</h2>
                {error && <p className="text-red-500 mb-4 text-sm bg-red-50 p-2 rounded">{error}</p>}
                <div className="space-y-4">
                    <div className="border p-3 rounded max-h-40 overflow-y-auto bg-gray-50">
                        {parcelasPendentes.map(p => (
                            <label key={p.id} className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded cursor-pointer">
                                <input type="checkbox" checked={selectedParcelaIds.includes(p.id)} onChange={() => handleSelectParcela(p.id)} className="w-4 h-4 text-blue-600 rounded" />
                                <span className="text-sm">Parcela {p.numero_parcela} - <strong>{formatCurrency(p.valor_parcela, selectedCurrency)}</strong></span>
                            </label>
                        ))}
                    </div>
                    
                    {/* CAMPO DE DATA DO RECEBIMENTO */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Data do Recebimento</label>
                        <input 
                            type="date" 
                            className="w-full p-2 border rounded" 
                            value={dataRecebimento} 
                            onChange={e => setDataRecebimento(e.target.value)} 
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Conta de Destino</label>
                        <select className="w-full p-2 border rounded" value={accountId} onChange={e => setAccountId(e.target.value)}>
                            <option value="">Selecione...</option>
                            {contasRecebimento.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <div className="flex-1"><label className="text-xs font-medium">Desconto</label><input type="number" step="0.01" className="w-full p-2 border rounded" value={descontoTotal} onChange={e => setDescontoTotal(parseFloat(e.target.value)||0)} /></div>
                        <div className="flex-1"><label className="text-xs font-medium">Juros</label><input type="number" step="0.01" className="w-full p-2 border rounded" value={jurosTotal} onChange={e => setJurosTotal(parseFloat(e.target.value)||0)} /></div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded text-right border border-blue-100">
                        <span className="text-sm text-blue-700">Total a Receber:</span>
                        <div className="font-bold text-xl text-blue-900">{formatCurrency(valorRecebidoTotal, selectedCurrency)}</div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancelar</button>
                    <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2">
                        {loading ? <Loader2 className="animate-spin" size={16} /> : 'Confirmar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL DA PÁGINA ---
const ContasAReceberPage = ({ accounts, onDataChange, formatCurrency, selectedCurrency, dataChangeKey, accountTypes, openNovoRecebimentoData }) => {
    const { apiFetch, userProfile, permissions } = useApi();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [faturas, setFaturas] = useState([]);
    
    // Estados de Paginação e Filtro
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Filtros
    const [filterMeioPagamento, setFilterMeioPagamento] = useState('');
    const [filterStatus, setFilterStatus] = useState('Todos');
    
    // Estado para alternar tipo de filtro de data
    const [filterDateType, setFilterDateType] = useState('month'); // 'month' ou 'date'
    const [filterDateValue, setFilterDateValue] = useState(() => {
        // Padrão: Mês atual
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    
    // Estados de Controle de Modais e Ações
    const [expandedFaturaId, setExpandedFaturaId] = useState(null); 
    const [deletingFaturaId, setDeletingFaturaId] = useState(null);
    const [editingFaturaId, setEditingFaturaId] = useState(null);
    const [isNovaFaturaOpen, setIsNovaFaturaOpen] = useState(false);
    const [isPagamentoOpen, setIsPagamentoOpen] = useState(false);
    const [selectedFatura, setSelectedFatura] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    
    const [initialRecebimentoData, setInitialRecebimentoData] = useState(null);

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const canCreate = permissions?.receivables_create || userProfile?.is_super_user;
    const canEdit = permissions?.receivables_edit || userProfile?.is_super_user;
    const canDelete = permissions?.receivables_delete || userProfile?.is_super_user;

    useEffect(() => {
        if (openNovoRecebimentoData) {
            setInitialRecebimentoData(openNovoRecebimentoData);
            setIsNovaFaturaOpen(true);
        }
    }, [openNovoRecebimentoData]);

    const fetchData = useCallback(async (page, search, filterMP, filterStatus, dateType, dateVal) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: page,
                limit: 12, // AJUSTADO AQUI PARA 12 RESULTADOS POR PÁGINA
                search: search,
                meio_pagamento: filterMP,
            });
            
            if (filterStatus && filterStatus !== 'Todos') {
                params.append('status', filterStatus);
            }
            
            // Lógica de envio de Data
            if (dateVal) {
                if (dateType === 'date') {
                    params.append('date', dateVal); // Envia dia exato
                } else {
                    params.append('month', dateVal); // Envia mês/ano
                }
            }
            
            const url = `/contas-a-receber?${params.toString()}`;
            const data = await apiFetch(url);
            
            const processedFaturas = data.faturas.map(f => ({
                ...f,
                parcelas: (typeof f.parcelas === 'string' ? JSON.parse(f.parcelas) : f.parcelas) || []
            }));
            
            setFaturas(processedFaturas);
            setTotalPages(data.totalPages || 1);
            
        } catch (err) {
            console.error("Erro ao buscar dados de contas a receber:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]); 

    // Atualiza quando filtros mudam
    useEffect(() => {
        fetchData(currentPage, debouncedSearchQuery, filterMeioPagamento, filterStatus, filterDateType, filterDateValue);
    }, [currentPage, debouncedSearchQuery, filterMeioPagamento, filterStatus, filterDateType, filterDateValue, dataChangeKey, fetchData]);

    // Reseta paginação se filtros mudarem
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery, filterMeioPagamento, filterStatus, filterDateType, filterDateValue]);


    const handleCloseModals = () => {
        setIsNovaFaturaOpen(false);
        setIsPagamentoOpen(false);
        setSelectedFatura(null);
        setEditingFaturaId(null);
        setDeletingFaturaId(null);
        setInitialRecebimentoData(null);
    };

    const handleSave = () => {
        handleCloseModals();
        onDataChange(); 
    };

    const handleOpenRecebimento = (fatura) => { 
        setSelectedFatura(fatura);
        setIsPagamentoOpen(true);
    };

    const handleOpenEditModal = (faturaId) => {
        setEditingFaturaId(faturaId);
    };

    const handleDeleteClick = (faturaId) => {
        setDeleteError('');
        setDeletingFaturaId(faturaId);
    };

    const handleConfirmDelete = async () => {
        if (!deletingFaturaId) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await apiFetch(`/contas-a-receber/fatura/${deletingFaturaId}`, { method: 'DELETE' });
            handleSave(); 
        } catch (err) {
            console.error("Erro ao excluir fatura:", err);
            setDeleteError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };
    
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const toggleFaturaExpand = (faturaId) => {
        if (expandedFaturaId === faturaId) setExpandedFaturaId(null);
        else setExpandedFaturaId(faturaId);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'paga': return <span className="px-2 py-0.5 text-xs font-medium text-green-800 bg-green-100 rounded-full"><CheckCircle size={12} className="inline mr-1" />Paga</span>;
            case 'vencida': return <span className="px-2 py-0.5 text-xs font-medium text-red-800 bg-red-100 rounded-full"><AlertTriangle size={12} className="inline mr-1" />Vencida</span>;
            default: return <span className="px-2 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded-full"><Info size={12} className="inline mr-1" />A Vencer</span>;
        }
    };
    
    const getMeioPagamentoIcon = (meio) => {
        switch (meio) {
            case 'pix': return <KeySquare size={16} className="text-green-500" title="PIX" />;
            case 'boleto': return <Receipt size={16} className="text-blue-500" title="Boleto" />;
            case 'dinheiro': return <Banknote size={16} className="text-gray-500" title="Dinheiro" />;
            default: return <DollarSign size={16} className="text-gray-400" />;
        }
    };

    return (
        <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Contas a Receber</h1>
                    <p className="text-gray-600">Gira as suas faturas e recebimentos de clientes.</p>
                </div>
                {canCreate && (
                    <button
                        onClick={() => setIsNovaFaturaOpen(true)}
                        className="flex items-center justify-center gap-2 px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                        <PlusCircle size={18} />
                        <span>Lançar Novo Recebimento</span>
                    </button>
                )}
            </div>

            {/* Barra de Filtro e Pesquisa */}
            <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    {/* Pesquisa (3 colunas) */}
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Pesquisar</label>
                        <div className="relative mt-1">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Nome, NF, OS/NSA..."
                                className="w-full p-2 pl-10 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                            <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>
                    
                    {/* Filtro de Data Híbrido (3 colunas) */}
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 flex justify-between">
                            <span>Período</span>
                            <button 
                                onClick={() => setFilterDateType(prev => prev === 'month' ? 'date' : 'month')}
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                title="Alternar entre Mês e Dia"
                            >
                                <RefreshCw size={10}/> {filterDateType === 'month' ? 'Mudar p/ Dia' : 'Mudar p/ Mês'}
                            </button>
                        </label>
                        <div className="relative mt-1">
                            <input
                                type={filterDateType === 'month' ? 'month' : 'date'}
                                value={filterDateValue}
                                onChange={e => setFilterDateValue(e.target.value)}
                                className="w-full p-2 pl-10 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                            <Calendar size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>

                    {/* Filtro Meio Pagamento (3 colunas) */}
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Meio de Pagamento</label>
                        <div className="relative mt-1">
                            <select value={filterMeioPagamento} onChange={e => setFilterMeioPagamento(e.target.value)} className="w-full p-2 pl-10 border rounded-md text-sm appearance-none bg-white focus:ring-blue-500 focus:border-blue-500">
                                <option value="">Todos</option>
                                <option value="pix">Pix</option>
                                <option value="boleto">Boleto</option>
                                <option value="dinheiro">Dinheiro</option>
                            </select>
                            <DollarSign size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>

                    {/* Filtro de Status (3 colunas) */}
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <div className="relative mt-1">
                            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full p-2 pl-10 border rounded-md text-sm appearance-none bg-white focus:ring-blue-500 focus:border-blue-500">
                                <option value="Todos">Todos</option>
                                <option value="a vencer">A Vencer</option>
                                <option value="vencida">Vencida</option>
                                <option value="paga">Paga</option>
                                <option disabled>──────────</option>
                                <option value="aguardando_envio">Aguardando Envio</option>
                                <option value="enviadas">Enviadas</option>
                            </select>
                            <Filter size={18} className="absolute left-3 top-2.5 text-gray-400" />
                        </div>
                    </div>
                </div>
            </div>

            {loading && <div className="flex justify-center items-center p-8"><Loader2 className="animate-spin text-blue-600" /></div>}
            {error && <p className="text-red-600 p-4 bg-red-100 rounded-md">{error}</p>}

            {!loading && !error && (
                <>
                    <div className="space-y-4">
                        {faturas.length === 0 ? (
                            <div className="p-8 text-center bg-white rounded-lg shadow border border-dashed border-gray-300">
                                <Info className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                                <h3 className="text-lg font-medium text-gray-900">Nenhum recebimento encontrado</h3>
                                <p className="text-gray-500 text-sm mt-1">Tente ajustar os filtros de data ou pesquisa.</p>
                            </div>
                        ) : (
                            faturas.map(fatura => (
                                <div key={fatura.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:border-blue-200 transition-colors">
                                    <div 
                                        className="flex flex-col md:flex-row justify-between md:items-center p-4 bg-gray-50 border-b cursor-pointer hover:bg-gray-100"
                                        onClick={() => toggleFaturaExpand(fatura.id)}
                                    >
                                        <div className="flex-1 mb-2 md:mb-0">
                                            <div className="flex items-center gap-2">
                                                {getMeioPagamentoIcon(fatura.parcelas[0]?.meio_pagamento)}
                                                <h2 className="text-lg font-semibold text-gray-800">{fatura.cliente_nome}</h2>
                                            </div>
                                            <div className="text-sm text-gray-500 mt-1 space-y-1">
                                                <p>
                                                    NF: <span className="font-medium text-gray-700">{fatura.nf_numero || 'N/A'}</span> | 
                                                    OS/NSA: <span className="font-medium text-gray-700">{fatura.os_nsa || 'N/A'}</span> | 
                                                    Emissão: <span className="font-medium text-gray-700">{formatDateUTC(fatura.data_emissao)}</span>
                                                </p>
                                                
                                                <div className="flex items-center gap-2 mt-1">
                                                    {fatura.data_envio ? (
                                                        <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200 flex items-center gap-1">
                                                            <Send size={10} /> Enviado em: {formatDateUTC(fatura.data_envio)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-200 flex items-center gap-1">
                                                            <AlertTriangle size={10} /> Aguardando envio
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <span className="text-xs text-gray-500 block uppercase tracking-wider">Total</span>
                                                <p className="text-lg font-bold text-gray-800">{formatCurrency(fatura.valor_total, selectedCurrency)}</p>
                                            </div>
                                            
                                            <div className="flex items-center gap-1">
                                                {canEdit && (
                                                    <button onClick={(e) => { e.stopPropagation(); handleOpenEditModal(fatura.id); }} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Editar">
                                                        <Edit size={18} />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(fatura.id); }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Excluir">
                                                        <Trash2 size={18} />
                                                    </button>
                                                )}
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); handleOpenRecebimento(fatura); }} 
                                                    disabled={!fatura.parcelas.some(p => p.status === 'a vencer' || p.status === 'vencida')} 
                                                    className="ml-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
                                                >
                                                    Baixar
                                                </button>
                                                <div className="ml-2 p-1 text-gray-400">
                                                    {expandedFaturaId === fatura.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {expandedFaturaId === fatura.id && (
                                        <div className="overflow-x-auto bg-gray-50/50 p-0 border-t border-gray-100">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">Status</th>
                                                            <th className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">Parcela</th>
                                                            <th className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">Vencimento</th>
                                                            <th className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">Valor</th>
                                                            <th className="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase tracking-wider">Pagamento</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {fatura.parcelas.map(p => (
                                                            <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(p.status)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.numero_parcela}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateUTC(p.data_vencimento)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{formatCurrency(p.valor_parcela, selectedCurrency)}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    {p.status === 'paga' ? (
                                                                        <span className="text-green-600 flex items-center gap-1"><CheckCircle size={14}/> {formatDateUTC(p.data_pagamento)}</span>
                                                                    ) : '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* PAGINAÇÃO ADICIONADA AQUI */}
                    {totalPages > 1 && faturas.length > 0 && (
                        <div className="mt-6 flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                            <span className="text-sm text-gray-500">
                                Página <span className="font-bold text-gray-800">{currentPage}</span> de <span className="font-bold text-gray-800">{totalPages}</span>
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium text-gray-700"
                                >
                                    <ChevronLeft size={16} /> Anterior
                                </button>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium text-gray-700"
                                >
                                    Próxima <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* --- MODAIS --- */}
            {(isNovaFaturaOpen || editingFaturaId) && (
                <NovoRecebimentoModal 
                    onClose={handleCloseModals}
                    onSave={handleSave}
                    formatCurrency={formatCurrency}
                    initialData={initialRecebimentoData}
                    faturaId={editingFaturaId}
                />
            )}

            {deletingFaturaId && <ConfirmDeleteModal onClose={handleCloseModals} onConfirm={handleConfirmDelete} loading={deleteLoading} error={deleteError} />}
            {isPagamentoOpen && selectedFatura && <RecebimentoModal onClose={handleCloseModals} onSave={handleSave} fatura={selectedFatura} accounts={accounts} accountTypes={accountTypes} formatCurrency={formatCurrency} selectedCurrency={selectedCurrency} />}
        </div>
    );
};

export default ContasAReceberPage;